import { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/Button";
import blogData from "../content/blogPosts.json";

const sortedPosts = [...blogData.posts].sort((a, b) => a.order - b.order);

const markdownComponents = {
  a: ({ href, children, ...props }) => {
    const external = href && (href.startsWith("http") || href.startsWith("//"));
    return (
      <a
        href={href}
        className="text-primary underline-offset-2 hover:underline font-medium"
        {...props}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {children}
      </a>
    );
  },
  table: ({ children }) => (
    <div className="my-6 w-full overflow-x-auto rounded-xl border border-border/60 bg-card/30">
      <table className="w-full min-w-[520px] text-sm border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-secondary/60">{children}</thead>,
  th: ({ children }) => (
    <th className="border-b border-border px-3 py-2 text-left font-semibold text-foreground">{children}</th>
  ),
  td: ({ children }) => <td className="border-b border-border/60 px-3 py-2 align-top text-muted-foreground">{children}</td>,
  tr: ({ children }) => <tr className="hover:bg-secondary/20 transition-colors">{children}</tr>,
  ul: ({ children }) => <ul className="my-4 list-disc pl-6 space-y-2 text-muted-foreground">{children}</ul>,
  ol: ({ children }) => <ol className="my-4 list-decimal pl-6 space-y-2 text-muted-foreground">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  h2: ({ children }) => (
    <h2 className="mt-10 mb-4 text-2xl font-bold text-foreground scroll-mt-28 border-b border-border/40 pb-2">{children}</h2>
  ),
  h3: ({ children }) => <h3 className="mt-8 mb-3 text-xl font-semibold text-foreground">{children}</h3>,
  p: ({ children }) => <p className="my-4 text-[15px] leading-relaxed text-muted-foreground">{children}</p>,
  hr: () => <hr className="my-8 border-border/50" />,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-primary/50 pl-4 my-6 italic text-muted-foreground">{children}</blockquote>
  ),
};

const BlogPostPage = () => {
  const { slug } = useParams();

  const postIndex = useMemo(() => sortedPosts.findIndex((p) => p.slug === slug), [slug]);
  const post = postIndex >= 0 ? sortedPosts[postIndex] : null;

  const bodyMarkdown = useMemo(() => {
    if (!post) return "";
    return post.markdown.replace(/^#\s[^\n]+\n*/, "");
  }, [post]);

  const prev = postIndex > 0 ? sortedPosts[postIndex - 1] : null;
  const next = postIndex >= 0 && postIndex < sortedPosts.length - 1 ? sortedPosts[postIndex + 1] : null;

  if (!slug || !post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-1 pt-20 sm:pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="flex flex-wrap gap-3 mb-8">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <ChevronLeft className="w-4 h-4" aria-hidden />
              Todos los artículos
            </Link>
          </div>

          <article className="rounded-3xl border border-border/60 bg-card/40 backdrop-blur-sm shadow-xl overflow-hidden">
            <header className="px-6 sm:px-10 pt-8 sm:pt-10 pb-6 border-b border-border/40 bg-gradient-to-br from-primary/5 to-accent/5">
              <p className="text-xs font-medium uppercase tracking-wider text-primary/90 mb-2">Mayo 2026 · Equipo GABI</p>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground leading-tight">{post.title}</h1>
            </header>
            <div className="px-6 sm:px-10 py-8 sm:py-10 prose prose-neutral dark:prose-invert max-w-none prose-headings:scroll-mt-24">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {bodyMarkdown}
              </ReactMarkdown>
            </div>
          </article>

          <nav className="mt-10 grid sm:grid-cols-2 gap-4" aria-label="Artículos anterior y siguiente">
            {prev ? (
              <Link to={`/blog/${prev.slug}`} className="block group">
                <div className="rounded-2xl border border-border/60 bg-card/30 p-5 h-full hover:border-primary/30 transition-colors">
                  <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    <ChevronLeft className="w-3 h-3" aria-hidden />
                    Anterior
                  </p>
                  <p className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                    {prev.title}
                  </p>
                </div>
              </Link>
            ) : (
              <div />
            )}
            {next ? (
              <Link to={`/blog/${next.slug}`} className="block group sm:text-right">
                <div className="rounded-2xl border border-border/60 bg-card/30 p-5 h-full hover:border-primary/30 transition-colors">
                  <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1 sm:justify-end">
                    Siguiente
                    <ChevronRight className="w-3 h-3" aria-hidden />
                  </p>
                  <p className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                    {next.title}
                  </p>
                </div>
              </Link>
            ) : null}
          </nav>

          <div className="mt-8 text-center">
            <Button variant="outline" size="sm" asChild>
              <Link to="/blog">Volver al blog</Link>
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPostPage;
